import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Learns each photo's shape before it is laid out.
 *
 * The spread has to know whether a photo is portrait or landscape to give it a
 * slot that fits — but nothing stores image dimensions, and reading them from
 * the full photo would mean downloading the gallery twice. So it measures the
 * thumbnail instead: ~11KB, the same aspect ratio as the original, and already
 * generated for every upload.
 *
 * Measuring runs in batches tied to how far the reader has scrolled, so a
 * 219-photo gallery does not fetch 219 thumbnails to render its first screen.
 */

// A thumbnail that never loads must not cost the reader the gallery: an
// unmeasured photo lays out as landscape, which is the safer default, rather
// than leaving the page blank waiting for a request that will not land.
const MEASURE_TIMEOUT_MS = 4000;

const loadRatio = (src) =>
  new Promise((resolve) => {
    if (!src) { resolve(null); return; }
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), MEASURE_TIMEOUT_MS);
    const img = new Image();
    img.onload = () => finish(img.naturalWidth > 0 ? img.naturalWidth / img.naturalHeight : null);
    img.onerror = () => finish(null);
    img.src = src;
  });

export default function useImageRatios(photos, batchSize = 16) {
  const [ratios, setRatios] = useState(() => new Map());
  const [measured, setMeasured] = useState(0);
  const [measuring, setMeasuring] = useState(false);
  const aliveRef = useRef(true);
  const busyRef = useRef(false);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // A new photo list invalidates everything measured so far.
  useEffect(() => {
    setRatios(new Map());
    setMeasured(0);
    busyRef.current = false;
  }, [photos]);

  const measureMore = useCallback(async () => {
    // busyRef rather than the measuring state: two calls in the same tick
    // (first paint plus an eager scroll) would both see measuring === false.
    if (busyRef.current) return;
    const list = photos || [];
    const next = list.slice(measured, measured + batchSize);
    if (next.length === 0) return;

    busyRef.current = true;
    setMeasuring(true);
    const results = await Promise.all(
      next.map((p) => loadRatio(p.thumbnail_url || p.image_url).then((r) => [p.id, r]))
    );
    if (!aliveRef.current) return;
    setRatios((prev) => {
      const merged = new Map(prev);
      for (const [id, r] of results) merged.set(id, r);
      return merged;
    });
    setMeasured((n) => n + next.length);
    busyRef.current = false;
    setMeasuring(false);
  }, [photos, measured, batchSize]);

  // First batch, as soon as there are photos to measure.
  useEffect(() => {
    if (photos && photos.length > 0 && measured === 0) measureMore();
  }, [photos, measured, measureMore]);

  const total = (photos || []).length;
  return {
    ratios,
    measured,
    measureMore,
    measuring,
    hasMore: measured < total,
    ready: measured > 0 || total === 0,
  };
}
